import React, { useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
interface Review {
  id: number;
  name: string;
  location: string;
  timeAgo: string;
  text: string;
}
const reviews: Review[] = [{
  id: 1,
  name: 'Sarah M.',
  location: 'Toronto',
  timeAgo: '2 weeks ago',
  text: 'EquityRich made the entire process incredibly simple. I was approved within 24 hours and had funds in my account two days later. No credit check, no hassle.'
}, {
  id: 2,
  name: 'Michael R.',
  location: 'Mississauga',
  timeAgo: '1 month ago',
  text: 'After being turned down by my bank, EquityRich helped me access my home equity quickly. The team was professional and transparent throughout.'
}, {
  id: 3,
  name: 'Jennifer L.',
  location: 'Brampton',
  timeAgo: '2 months ago',
  text: "I used EquityRich for debt consolidation and it was the best financial decision I've made. Lower payments, one simple bill."
}, {
  id: 4,
  name: 'David K.',
  location: 'Vaughan',
  timeAgo: '3 months ago',
  text: 'The no-income-verification process was a game changer for me as a self-employed contractor. Highly recommend!'
}, {
  id: 5,
  name: 'Patricia W.',
  location: 'Markham',
  timeAgo: '4 months ago',
  text: 'Excellent service from start to finish. They explained everything clearly and there were no hidden fees.'
}, {
  id: 6,
  name: 'Robert C.',
  location: 'Oakville',
  timeAgo: '5 months ago',
  text: 'Got my second mortgage approved in record time. The rates were competitive and the team was always available to answer questions.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function GoogleReviews() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  const scrollByCard = () => {
    if (scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const cardWidth = (containerWidth - 48) / 3 + 24; // card width + one gap
      return cardWidth;
    }
    return 380;
  };
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -scrollByCard(),
        behavior: 'smooth'
      });
    }
  };
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollByCard(),
        behavior: 'smooth'
      });
    }
  };
  return <section className="w-full bg-cream py-20 px-4 overflow-hidden" data-id="element-145">
      <div className="max-w-7xl mx-auto" data-id="element-146">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-147">
          <span className="inline-block px-4 py-2 text-[12px] font-[Poppins,_sans-serif] text-[#07493a] bg-[#07493a1a] font-medium rounded-full mb-4 border border-primary/20 pt-[4px] pb-[4px] pl-[18px] pr-[18px]" data-id="element-148">
            WHAT OUR CLIENTS SAY
          </span>
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6 font-[500]" data-id="element-149">
            Trusted by Homeowners Across the GTA
          </h2>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-150">
            <div className="flex items-center gap-2" data-id="element-151">
              <span className="text-3xl font-bold text-gray-900" data-id="element-152">4.9</span>
              <div className="flex text-[#FBBC05]" data-id="element-153">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-154" />)}
              </div>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-gray-300 rounded-full" data-id="element-155"></div>
            <div className="flex items-center gap-2" data-id="element-156">
              <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg" data-id="element-157">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-158" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-159" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-160" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-161" />
              </svg>
              <span className="text-gray-600 font-medium" data-id="element-162">
                Based on 150+ Google Reviews
              </span>
            </div>
          </div>
        </div>

        {/* Reviews Slider */}
        <div className="relative" data-id="element-163">
          {/* Left Arrow */}
          {canScrollLeft && <button onClick={scrollLeft} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:shadow-lg hover:scale-105 transition-all duration-200" aria-label="Scroll left" data-id="element-164">
              <ChevronLeft className="w-5 h-5 text-gray-700" data-id="element-165" />
            </button>}

          {/* Right Arrow */}
          {canScrollRight && <button onClick={scrollRight} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:shadow-lg hover:scale-105 transition-all duration-200" aria-label="Scroll right" data-id="element-166">
              <ChevronRight className="w-5 h-5 text-gray-700" data-id="element-167" />
            </button>}

          <motion.div ref={scrollContainerRef} onScroll={handleScroll} className="flex gap-6 overflow-x-auto pb-4" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5
        }} data-id="element-168">
            {reviews.map(review => <div key={review.id} className="bg-white rounded-2xl shadow-sm p-8 flex flex-col flex-shrink-0 hover:shadow-md transition-shadow duration-300" style={{
            width: 'calc((100% - 48px) / 3)',
            minWidth: '300px'
          }} data-id="element-169">
                <div className="flex text-[#FBBC05] mb-4" data-id="element-170">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-171" />)}
                </div>

                <p className="text-gray-700 leading-relaxed mb-8 flex-1" data-id="element-172">
                  "{review.text}"
                </p>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100" data-id="element-173">
                  <div data-id="element-174">
                    <h4 className="font-bold text-gray-900" data-id="element-175">{review.name}</h4>
                    <span className="text-sm text-gray-500" data-id="element-176">
                      {review.timeAgo}
                    </span>
                  </div>
                  <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" className="opacity-80" data-id="element-177">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-178" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-179" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-180" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-181" />
                  </svg>
                </div>
              </div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}